import { File as FileIcon } from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { ImageSlot } from '../../../ds/components';
import { portalDocumentHref } from '../api';
import type { PortalOverviewAfter } from '../types';
import { PortalAction } from './actions';
import styles from './Overview.module.css';

/** Portal 15 unit card: neutral tile (the model has no unit photo for the portal), title, pill, line, text. */
export function OwnedUnitCard({
  unit,
  preview,
  withObjectLink,
}: {
  unit: NonNullable<PortalOverviewAfter['unit']>;
  preview?: boolean | undefined;
  withObjectLink: boolean;
}) {
  return (
    <section className={clsx(styles.card, styles.unitOwned)} aria-label={unit.title}>
      <div className={styles.unitImage}>
        <ImageSlot alt={unit.title} placeholder={unit.title} height={220} radius="none" chrome="frame" />
      </div>
      <div className={styles.unitOwnedBody}>
        <div className={styles.unitOwnedTop}>
          <span className={styles.unitOwnedTitle}>{unit.title}</span>
          <span className={clsx(styles.pill, styles.pillSuccess)}>{unit.pill}</span>
        </div>
        <div className={styles.unitOwnedLine}>{unit.line}</div>
        <p className={styles.unitOwnedText}>{unit.text}</p>
        {withObjectLink ? (
          <div className={styles.unitOwnedButtons}>
            <PortalAction
              target={{ section: 'objects' }}
              preview={preview}
              variant="secondary"
              className={styles.smallSecondary}
            >
              Objekt ansehen
            </PortalAction>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Mandantenportal 15 „Übersicht · Bestandskunde nach Kauf": greeting, three KPIs, the unit, recent documents. */
export function OwnerOverview({
  data,
  preview,
}: {
  data: PortalOverviewAfter;
  preview?: boolean | undefined;
}) {
  return (
    <div className={clsx(styles.overview, styles.gapOwner)}>
      <div>
        <div className={styles.eyebrow}>Ihr Eigentum</div>
        <h1 className={styles.ownerTitle}>{data.greeting}</h1>
      </div>
      <div className={styles.kpis}>
        {data.kpis.map((kpi) => (
          <div key={kpi.label} className={clsx(styles.card, styles.kpi)}>
            <div className={styles.eyebrow}>{kpi.label}</div>
            <div className={styles.kpiValue}>{kpi.value}</div>
            {kpi.sub ? <div className={styles.kpiSub}>{kpi.sub}</div> : null}
          </div>
        ))}
      </div>
      <div className={clsx(styles.split, styles.splitTight)}>
        {data.unit ? <OwnedUnitCard unit={data.unit} preview={preview} withObjectLink /> : null}
        <section className={clsx(styles.card, styles.recent)} aria-labelledby="portal-recent">
          <h2 className={styles.eyebrow} id="portal-recent">
            Zuletzt für Sie freigegeben
          </h2>
          {data.recentDocuments.length === 0 ? (
            <p className={styles.emptyNote}>Noch keine Dokumente freigegeben.</p>
          ) : (
            <ul className={styles.recentList}>
              {data.recentDocuments.map((document) => (
                <li key={document.id}>
                  {preview ? (
                    <div className={styles.recentRow}>
                      <FileIcon size={18} strokeWidth={1.5} aria-hidden="true" />
                      <div className={styles.recentText}>
                        <div className={styles.recentName}>{document.name}</div>
                        <div className={styles.recentMeta}>{document.meta}</div>
                      </div>
                    </div>
                  ) : (
                    <a className={styles.recentRow} href={portalDocumentHref(document.id)} download>
                      <FileIcon size={18} strokeWidth={1.5} aria-hidden="true" />
                      <div className={styles.recentText}>
                        <div className={styles.recentName}>{document.name}</div>
                        <div className={styles.recentMeta}>{document.meta}</div>
                      </div>
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
          <PortalAction
            target={{ section: 'documents' }}
            preview={preview}
            variant="link"
            className={styles.recentAll}
          >
            Alle Dokumente
          </PortalAction>
        </section>
      </div>
    </div>
  );
}
